import { FirestoreNotamFeature, UserTier } from "@weatheredstrip/shared";
import { useAuthUser } from "../hooks/index";
import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { FirebaseContext } from "../firebase";
import {
  CollectionReference,
  collection,
  getDocs,
  onSnapshot,
} from "firebase/firestore";

interface IGpsNotamContext {
  data: FirestoreNotamFeature[];
}

const GPSNotamsContext = createContext({} as IGpsNotamContext);

export const GpsNotamsProvider = ({ children }: { children: ReactNode }) => {
  const firebase = useContext(FirebaseContext);
  const auth = useAuthUser();
  const [gpsNotams, setGpsNotams] = useState<FirestoreNotamFeature[]>([]);

  const profileTier = auth?.profile?.tier ?? UserTier.Basic;

  useEffect(() => {
    if (!firebase.firestore) return;

    const gpsNotamsCollection = collection(
      firebase.firestore,
      "gps_notams"
    ) as CollectionReference<FirestoreNotamFeature, FirestoreNotamFeature>;

    if (profileTier >= UserTier.Paid) {
      const unsubscribe = onSnapshot(gpsNotamsCollection, (snapshot) => {
        const records: FirestoreNotamFeature[] = [];
        snapshot.forEach((record) => {
          records.push(record.data());
        });
        setGpsNotams(records);
      });

      return () => {
        unsubscribe();
      };
    } else {
      getDocs(gpsNotamsCollection).then((snapshot) => {
        const records: FirestoreNotamFeature[] = [];
        snapshot.forEach((record) => {
          records.push(record.data());
        });
        setGpsNotams(records);
      });

      return () => {};
    }
  }, [profileTier, firebase.firestore]);

  return (
    <GPSNotamsContext.Provider value={{ data: gpsNotams }}>
      {children}
    </GPSNotamsContext.Provider>
  );
};

export const TestGpsNotamsProvider = ({
  children,
  fixtureGpsNotam = [],
}: {
  children: ReactNode;
  fixtureGpsNotam?: FirestoreNotamFeature[];
}) => {
  return (
    <GPSNotamsContext.Provider value={{ data: fixtureGpsNotam }}>
      {children}
    </GPSNotamsContext.Provider>
  );
};

export const useGpsNotams = (): IGpsNotamContext => {
  const context = useContext(GPSNotamsContext);

  if (!Object.keys(context).length) {
    // if context is empty, we are not within a GpsNotamsProvider
    throw new Error("useGpsNotams must be used within a GpsNotamsProvider");
  }

  return context;
};
